/*
 * @Author: Zhouqi
 * @Date: 2026-03-12 15:08:13
 * @LastEditors: Zhouqi
 * @LastEditTime: 2026-03-19 11:23:53
 */
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import DistributionStore from '@/components/UI/Distribution/store';
import { DistributionCodeData } from '@/components/UIComponents/CommonModal/distribution/hooks/useDistributionCode';
import { getDistributionCode } from '@/components/UIComponents/CommonModal/service';
import { exchangeCode } from '@/components/UIComponents/UserAccount/service';
import useLoginStore from '@/store/Login';
import UserStore from '@/store/User';
import { EventNewID, RegisterTypeEnum } from '@/utils/eventlog/eventNewConfig';
import { setUserProfileDirect, trackEvent } from '@/utils/eventlog/logAdapter';

// 定义 exchangeCode 返回的数据类型
interface ExchangeCodeData {
  type?: number;
}

/**
 * 处理新用户邀请码绑定的 Hook
 *
 * 功能：
 * - 监听用户登录成功事件
 * - 检测 URL 中的 inviteCode 参数
 * - 自动绑定邀请码
 * - 显示绑定成功提示
 */
export const useInviteCodeBinding = () => {
  const router = useRouter();
  const hasBindRef = useRef(false);

  useEffect(() => {
    const handleLoginSuccess = async () => {
      // 防止重复绑定
      if (hasBindRef.current) {
        return;
      }

      // 检查 localStorage 是否已绑定过
      const userPurchaseFlag = localStorage.getItem('userPurchaseFlag');
      if (userPurchaseFlag === 'true') {
        return;
      }

      // 获取 URL 参数
      const { inviteCode } = router.query;
      const isNewUser = useLoginStore.getState().isNewUser;

      // 新用户上报注册成功埋点，并设置 register_type 用户属性
      if (isNewUser) {
        const code = (inviteCode as string) || '';
        const registerType = code ? RegisterTypeEnum.INVITE : RegisterTypeEnum.NORMAL;
        trackEvent(EventNewID.REGISTER_SUCCESS, {
          inviteCode: code,
          registerType,
        });
        setUserProfileDirect({ register_type: registerType });
      } else {
        // 老用户默认 normal
        setUserProfileDirect({ register_type: RegisterTypeEnum.NORMAL });
      }

      // 只有新用户且有邀请码时才绑定
      if (!inviteCode || !isNewUser) {
        return;
      }

      try {
        hasBindRef.current = true;

        // 调用绑定接口
        const res = await exchangeCode<ExchangeCodeData>(inviteCode as string, true);

        if (res.success) {
          // 绑定成功
          const data = Array.isArray(res.data) ? res.data[0] : res.data;
          if (data?.type === 2) {
            // 分销码绑定成功
            // 标记已绑定
            localStorage.setItem('userPurchaseFlag', 'true');

            // 刷新用户信息
            await UserStore.getState().getUserBenefit();

            // 查询绑定的分销码，获取邀请人的 pattern 来决定积分数
            const boundCode = await getDistributionCode<DistributionCodeData>(2);
            const inviterPattern =
              boundCode.success && boundCode.data && !Array.isArray(boundCode.data)
                ? boundCode.data.pattern
                : undefined;
            const credits = inviterPattern === 2 ? 30 : 20;

            // 显示成功提示
            DistributionStore.getState().setNewUserCredits(credits);
            DistributionStore.getState().setShowNewUserMode2Tip(true);

            // 记录成功状态
            localStorage.setItem('inviteSuccess', 'true');
          }
        } else {
          // 绑定失败，显示错误提示
          console.error('邀请码绑定失败:', res.message);
          // 不显示错误提示给用户，避免影响体验
        }
      } catch (error) {
        console.error('邀请码绑定异常:', error);
      }
    };

    // 监听登录成功事件
    window.addEventListener('userLoginSuccess', handleLoginSuccess);

    return () => {
      window.removeEventListener('userLoginSuccess', handleLoginSuccess);
    };
  }, [router.query]);

  return null;
};
